import { Button, Flex, H1, Tabs } from '@dnb/eufemia';
import { TabsData } from '@dnb/eufemia/components/tabs';
import { launch as LaunchIcon } from '@dnb/eufemia/icons';
import { ApiType, ApiWithStatusAndScopesDto } from '@portals/shared/portal/ApiDto';
import { TrackRoute } from '@portals/shared-frontend/components';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, Outlet, useLocation, useNavigate, useParams, useResolvedPath } from 'react-router-dom';
import useSWR, { KeyedMutator } from 'swr';

import BackButton from '@/components/BackButton';
import HeroPage from '@/components/HeroPage';
import { HeroContent } from '@/components/HeroPage/HeroContent';
import { useUser } from '@/hooks/useUser';

export interface ApiProviderPageOutletContext {
  api: ApiWithStatusAndScopesDto;
  mutate: KeyedMutator<ApiWithStatusAndScopesDto>;
  isTeamAdmin: boolean;
  showCiamClients: boolean;
  setShowCiamClients: (show: boolean) => void;
}

enum tabKey {
  Overview = 'overview',
  LifecycleManagement = 'lifecycle-management',
  VisibilityAccess = 'visibility-access',
  Consumers = 'consumers',
}

const apiTabs: TabsData = [
  { title: 'Overview', key: tabKey.Overview },
  { title: 'Lifecycle Management', key: tabKey.LifecycleManagement },
  { title: 'Visibility & Access', key: tabKey.VisibilityAccess },
  { title: 'Consumers', key: tabKey.Consumers },
];

const eventTabs: TabsData = [
  { title: 'Overview', key: tabKey.Overview },
  { title: 'Visibility & Access', key: tabKey.VisibilityAccess },
  { title: 'Consumers', key: tabKey.Consumers },
];

const mcpTabs: TabsData = [
  { title: 'Overview', key: tabKey.Overview },
  { title: 'Visibility & Access', key: tabKey.VisibilityAccess },
];

const tabsByApiType: Record<ApiType, TabsData> = {
  [ApiType.REST]: apiTabs,
  [ApiType.EVENT]: eventTabs,
  [ApiType.MCP]: mcpTabs,
};

export function ApiProviderPage() {
  const { appId, apiId, teamId } = useParams();
  const { pathname: resolvedPathname } = useResolvedPath('.');
  const { pathname: locationPathname } = useLocation();
  const { user } = useUser();
  const isTeamAdmin = user?.teams.some((team) => team.id === teamId && team.isAdmin) ?? false;
  const [showCiamClients, setShowCiamClients] = useState(false);

  const navigate = useNavigate();
  const {
    data: api,
    isValidating: loadingApi,
    mutate,
  } = useSWR<ApiWithStatusAndScopesDto>(appId && apiId && `/apps/${appId}/apis/${apiId}`);

  // Get team name and app name from user data for breadcrumbs
  const teamName = user?.teams.find((team) => team.id === teamId)?.name || 'Team';

  const onTabChange = useCallback(
    ({ key }: { key: string }) => {
      if (key === tabKey.Overview) navigate('');
      else navigate(key);
    },
    [navigate],
  );

  const activeTab = useMemo(
    () => locationPathname.replace(resolvedPathname, '').split('/')[1] ?? tabKey.Overview,
    [locationPathname, resolvedPathname],
  );

  const filteredTabsData = (api ? tabsByApiType[api.type] : null) ?? eventTabs;

  // Redirect to overview if the current route is not allowed for this API type
  useEffect(() => {
    if (!api) return;
    const validKeys = filteredTabsData.map((tab: { key: string }) => tab.key);
    const effectiveTab = activeTab || tabKey.Overview;
    if (!validKeys.includes(effectiveTab)) {
      navigate('', { replace: true });
    }
  }, [api, activeTab, filteredTabsData, navigate]);

  const headerContent = useMemo(
    () => (
      <Flex.Vertical gap={false} rowGap={false} stretch>
        <Flex.Container align="center" justify="space-between" stretch>
          <H1 size="x-large">{api?.name}</H1>
          {api?.slug && (
            <Button
              element={RouterLink}
              icon={<LaunchIcon />}
              target="_blank"
              to={
                api?.type === ApiType.EVENT
                  ? `/events/${api.slug}/@default/@latest/schemas`
                  : api?.type === ApiType.MCP
                    ? `/mcp-servers/${api.slug}/@default/@latest/guide`
                    : `/documentation/${api.slug}/@default/@latest`
              }
              tooltip="Opens the documentation in a new tab"
              variant="secondary"
            >
              View documentation
            </Button>
          )}
        </Flex.Container>
      </Flex.Vertical>
    ),
    [api?.name, api?.slug, api?.type],
  );
  return (
    <HeroPage banner="personal" heroLeftComponent={headerContent} noTopMargin skeleton={loadingApi} title={api?.name}>
      {api && teamId && teamName && (
        <TrackRoute
          name={api.name}
          parents={[
            { path: `/team/${teamId}`, name: teamName, type: 'team' },
            { path: `/team/${teamId}/application/${appId}`, name: 'App', type: 'app' },
          ]}
          path={`/team/${teamId}/application/${appId}/api/${apiId}`}
          type={api.type === ApiType.EVENT ? 'event-mgmt' : api.type === ApiType.MCP ? 'mcp-mgmt' : 'api-mgmt'}
        />
      )}
      <HeroContent>
        <BackButton bottom="large" to="./../..">
          Back to app page
        </BackButton>
        <Tabs
          bottom="large"
          breakout={false}
          data={filteredTabsData}
          on_change={onTabChange}
          selected_key={activeTab}
        />
        {api && (
          <Outlet
            context={
              { api, mutate, isTeamAdmin, showCiamClients, setShowCiamClients } satisfies ApiProviderPageOutletContext
            }
          />
        )}
      </HeroContent>
    </HeroPage>
  );
}
